import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableRoundItem } from './SortableRoundItem';
import type { PipelineRound } from '../types';

interface RoundListProps {
  rounds: PipelineRound[];
  onEdit: (round: PipelineRound) => void;
  onDelete: (round: PipelineRound) => void;
  onReorder: (roundIds: string[]) => Promise<void>;
  deletingIds: Set<string>;
}

export function RoundList({ rounds, onEdit, onDelete, onReorder, deletingIds }: RoundListProps) {
  const { t } = useTranslation('ats');
  const [localRounds, setLocalRounds] = useState<PipelineRound[]>(rounds);
  const [isReordering, setIsReordering] = useState(false);

  // Update local state when rounds prop changes (compare full data, not just IDs)
  if (JSON.stringify(rounds) !== JSON.stringify(localRounds)) {
    setLocalRounds(rounds);
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localRounds.findIndex((r) => r.id === active.id);
      const newIndex = localRounds.findIndex((r) => r.id === over.id);

      const draggedRound = localRounds[oldIndex];

      // Prevent moving default rounds (APPLICATION_RECEIVED must stay first, HIRED must stay last)
      if (
        draggedRound.round_type === 'application_received' ||
        draggedRound.round_type === 'hired'
      ) {
        return; // Don't allow dragging default rounds
      }

      // Prevent dropping in first position (reserved for APPLICATION_RECEIVED)
      if (newIndex === 0) {
        return;
      }

      // Prevent dropping in last position (reserved for HIRED)
      if (newIndex === localRounds.length - 1) {
        return;
      }

      // Optimistic update
      const newRounds = arrayMove(localRounds, oldIndex, newIndex);
      setLocalRounds(newRounds);

      // Call API to persist the reorder
      setIsReordering(true);
      try {
        await onReorder(newRounds.map((r) => r.id));
      } catch {
        // Revert on error
        setLocalRounds(rounds);
      } finally {
        setIsReordering(false);
      }
    }
  };

  if (localRounds.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
        <p className="text-gray-500">{t('pipeline.roundList.empty')}</p>
        <p className="text-sm text-gray-400 mt-1">{t('pipeline.roundList.emptyHint')}</p>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={localRounds.map((r) => r.id)}
        strategy={verticalListSortingStrategy}
        disabled={isReordering}
      >
        <div className="space-y-2">
          {localRounds.map((round) => (
            <SortableRoundItem
              key={round.id}
              round={round}
              onEdit={onEdit}
              onDelete={onDelete}
              isDeleting={deletingIds.has(round.id)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
